import React from 'react';

// ── Types (local — ExamModeWidget also exports the full config type) ──

export type ResponseType = 'short_answer' | 'extended_response' | 'working_out' | 'essay';

export type QuestionConfig = {
  id: string;
  marks: number;
  stimulus?: string;
  stimulusType?: 'text' | 'image' | 'quote';
  stimulusImageUrl?: string;
  prompt: string;
  responseType: ResponseType;
  minWords?: number;
  maxWords?: number;
  markingCriteria?: string[];
};

interface Props {
  question: QuestionConfig;
  questionNumber: number | string;
  response: string;
  onChange: (questionId: string, value: string) => void;
  readOnly: boolean;
  showMarkingCriteria: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────

function countWords(text: string): number {
  return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
}

const TEXTAREA_ROWS: Record<ResponseType, number> = {
  short_answer:      5,
  extended_response: 12,
  working_out:       18,
  essay:             22,
};

const TEXTAREA_PLACEHOLDER: Record<ResponseType, string> = {
  short_answer:      'Write your answer here…',
  extended_response: 'Write your response here…',
  working_out:       'Show all working here… (e.g.  Mean = (3.5 + 5.0 + …) / 8)',
  essay:             'Begin your writing here…',
};

// ── Component ──────────────────────────────────────────────────────

export function QuestionBlock({
  question,
  questionNumber,
  response,
  onChange,
  readOnly,
  showMarkingCriteria,
}: Props) {
  const wordCount = countWords(response);
  const rows = TEXTAREA_ROWS[question.responseType];
  const isMonospace = question.responseType === 'working_out';

  // Word count status
  let wcStatus: 'under' | 'ok' | 'over' = 'under';
  if (question.minWords != null && wordCount >= question.minWords) wcStatus = 'ok';
  if (question.maxWords != null && wordCount > question.maxWords) wcStatus = 'over';
  const showWordCount = question.minWords != null || question.maxWords != null;

  return (
    <div className="qb-root">
      {/* Header: question number + marks */}
      <div className="qb-header">
        <span className="qb-num">Question {questionNumber}</span>
        <span className="qb-marks">{question.marks} {question.marks === 1 ? 'mark' : 'marks'}</span>
      </div>

      {/* Stimulus block */}
      {question.stimulus && (
        <div className={`qb-stimulus qb-stimulus--${question.stimulusType ?? 'text'}`}>
          {question.stimulusType === 'image' && question.stimulusImageUrl ? (
            <img src={question.stimulusImageUrl} alt="Stimulus" className="qb-stimulus-img" />
          ) : (
            <div className="qb-stimulus-text">{question.stimulus}</div>
          )}
        </div>
      )}

      {/* Question prompt */}
      <p className="qb-prompt">{question.prompt}</p>

      {/* Response area */}
      <textarea
        className={[
          'qb-textarea',
          `qb-textarea--${question.responseType}`,
          isMonospace ? 'qb-textarea--mono' : '',
        ].filter(Boolean).join(' ')}
        rows={rows}
        value={response}
        onChange={e => onChange(question.id, e.target.value)}
        readOnly={readOnly}
        placeholder={readOnly ? '' : TEXTAREA_PLACEHOLDER[question.responseType]}
        aria-label={`Response for question ${questionNumber}`}
        spellCheck={question.responseType !== 'working_out'}
      />

      {/* Word counter */}
      {showWordCount && (
        <p className={`qb-word-count qb-word-count--${wcStatus}`}>
          {wordCount} {wordCount === 1 ? 'word' : 'words'}
          {question.minWords != null && ` / ${question.minWords} minimum`}
          {question.maxWords != null && ` (max ${question.maxWords})`}
        </p>
      )}

      {/* Marking criteria (post-submit self-assessment) */}
      {showMarkingCriteria && question.markingCriteria && question.markingCriteria.length > 0 && (
        <div className="qb-criteria">
          <p className="qb-criteria-heading">Marking Criteria — self-assess your response</p>
          <ul className="qb-criteria-list">
            {question.markingCriteria.map((criterion, i) => (
              <li key={i}>{criterion}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
